package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.*
import com.sddamico.util.formatPoints
import com.sddamico.util.sportDisplayName
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.LineHeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

private val tradeIndigo = Color.rgb(0x6366F1)
private val tradeIndigoBg = Color.rgb(0x312E81)
private val outgoingGray = Color.rgb(0x6B7280)

@Composable
fun TradeOfferCard(
    trade: TradeOfferResponse,
    userClubId: String,
    leagueYearId: String?,
    onRefresh: () -> Unit,
    onRosterRefresh: () -> Unit,
    readOnly: Boolean = false,
) {
    val scope = rememberCoroutineScope()
    val isIncoming = trade.receiverClubId == userClubId
    val counterpartyName = if (isIncoming) trade.senderClubName else trade.receiverClubName
    val badgeLabel = if (isIncoming) "Incoming Trade" else "Outgoing Trade"
    val badgeColor = if (isIncoming) tradeIndigo else outgoingGray

    val senderAssets = trade.assets.filter { it.fromClubId == trade.senderClubId }
    val receiverAssets = trade.assets.filter { it.fromClubId == trade.receiverClubId }

    var processing by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var showAcceptModal by remember { mutableStateOf(false) }

    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, badgeColor)
            .borderRadius(8.px)
            .padding(1.cssRem)
            .toAttrs()
    ) {
        Column(Modifier.gap(0.5.cssRem)) {
            // Badge + counterparty
            Row(Modifier.gap(0.5.cssRem).fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Div(
                    Modifier
                        .backgroundColor(badgeColor)
                        .borderRadius(4.px)
                        .padding(topBottom = 0.125.cssRem, leftRight = 0.5.cssRem)
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .toAttrs()
                ) {
                    SpanText(
                        badgeLabel,
                        Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold).color(Colors.White)
                            .lineHeight(LineHeight.Normal)
                    )
                }
                SpanText(
                    if (isIncoming) "from $counterpartyName" else "to $counterpartyName",
                    Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
                )
            }

            val youSendLabel = if (isIncoming) "They request" else "You send"
            val youReceiveLabel = if (isIncoming) "$counterpartyName sends" else "You receive"
            val assetsYouGet = if (isIncoming) senderAssets else receiverAssets
            val assetsYouGive = if (isIncoming) receiverAssets else senderAssets

            Column(Modifier.gap(0.125.cssRem)) {
                SpanText(
                    youSendLabel,
                    Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.textMuted)
                )
                assetsYouGive.forEach { asset ->
                    TradeAssetRow(asset, gain = false)
                }
            }

            Column(Modifier.gap(0.125.cssRem)) {
                SpanText(
                    youReceiveLabel,
                    Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.textMuted)
                )
                assetsYouGet.forEach { asset ->
                    TradeAssetRow(asset, gain = true)
                }
            }

            // Metadata
            SpanText(
                "Offered: ${trade.createdAt.substringBefore("T")}",
                Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
            )

            if (trade.notes != null) {
                SpanText(
                    trade.notes,
                    Modifier.fontSize(0.8125.cssRem).fontStyle(FontStyle.Italic).color(SitePalette.textSecondary)
                )
            }

            if (errorMessage != null) {
                SpanText(errorMessage!!, Modifier.fontSize(0.75.cssRem).color(SitePalette.error))
            }

            // Action buttons
            if (readOnly) {
                // No action buttons in read-only mode
            } else if (isIncoming) {
                Row(Modifier.margin(top = 0.375.cssRem).gap(0.5.cssRem).fillMaxWidth().position(Position.Relative)) {
                    // Accept
                    ConfirmableButton(
                        label = "Accept",
                        loadingLabel = "Processing...",
                        confirmLabel = "Are you sure?",
                        isLoading = processing,
                        buttonModifier = Modifier
                            .backgroundColor(if (processing) SitePalette.border else SitePalette.primary)
                            .color(Colors.White)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .transition(Transition.of("background-color", 150.ms)),
                        onConfirm = {
                            scope.launch {
                                processing = true
                                errorMessage = null
                                try {
                                    val body = apiJson.encodeToString(AcceptTradeOfferRequest(trade.id))
                                    apiPost("/api/trades/accept", body, TradeOfferResponse.serializer())
                                    onRefresh()
                                    onRosterRefresh()
                                } catch (e: ApiException) {
                                    // Check if the error is about needing to drop players
                                    if (e.errorMessage?.contains("slot") == true || e.errorMessage?.contains("drop") == true) {
                                        showAcceptModal = true
                                        errorMessage = null
                                    } else {
                                        errorMessage = e.errorMessage
                                    }
                                } catch (_: Exception) {
                                    errorMessage = "Failed to accept trade"
                                }
                                processing = false
                            }
                        },
                    )

                    // Decline
                    ConfirmableButton(
                        label = "Decline",
                        loadingLabel = "Processing...",
                        confirmLabel = "Are you sure?",
                        isLoading = processing,
                        buttonModifier = Modifier
                            .backgroundColor(if (processing) SitePalette.border else SitePalette.error)
                            .color(Colors.White)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .transition(Transition.of("background-color", 150.ms)),
                        onConfirm = {
                            scope.launch {
                                processing = true
                                errorMessage = null
                                try {
                                    val body = apiJson.encodeToString(DeclineTradeOfferRequest(trade.id))
                                    apiPost("/api/trades/decline", body, TradeOfferResponse.serializer())
                                    onRefresh()
                                } catch (e: ApiException) {
                                    errorMessage = e.errorMessage
                                } catch (_: Exception) {
                                    errorMessage = "Failed to decline trade"
                                }
                                processing = false
                            }
                        },
                    )
                }
            } else {
                // Outgoing: Cancel button
                Row(Modifier.margin(top = 0.375.cssRem).fillMaxWidth().position(Position.Relative)) {
                    ConfirmableButton(
                        label = "Cancel Offer",
                        loadingLabel = "Cancelling...",
                        confirmLabel = "Are you sure?",
                        isLoading = processing,
                        buttonModifier = Modifier
                            .backgroundColor(if (processing) SitePalette.border else SitePalette.error)
                            .color(Colors.White)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .transition(Transition.of("background-color", 150.ms)),
                        onConfirm = {
                            scope.launch {
                                processing = true
                                errorMessage = null
                                try {
                                    val body = apiJson.encodeToString(CancelTradeOfferRequest(trade.id))
                                    apiPost("/api/trades/cancel", body, TradeOfferResponse.serializer())
                                    onRefresh()
                                } catch (e: ApiException) {
                                    errorMessage = e.errorMessage
                                } catch (_: Exception) {
                                    errorMessage = "Failed to cancel trade"
                                }
                                processing = false
                            }
                        },
                    )
                }
            }
        }
    }

    // Accept modal for when drops are needed
    if (showAcceptModal && leagueYearId != null) {
        TradeAcceptModal(
            trade = trade,
            userClubId = userClubId,
            leagueYearId = leagueYearId,
            onClose = { showAcceptModal = false },
            onSuccess = {
                showAcceptModal = false
                onRefresh()
                onRosterRefresh()
            },
        )
    }
}

@Composable
private fun TradeAcceptModal(
    trade: TradeOfferResponse,
    userClubId: String,
    leagueYearId: String,
    onClose: () -> Unit,
    onSuccess: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var roster by remember { mutableStateOf<ClubRosterResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var submitting by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val selectedDropIds = remember { mutableStateListOf<String>() }

    // Load roster
    LaunchedEffect(Unit) {
        try {
            roster = apiGet(
                "/api/clubs/roster?clubId=$userClubId&leagueYearId=$leagueYearId",
                ClubRosterResponse.serializer()
            )
        } catch (_: Exception) {
            errorMessage = "Failed to load roster"
        }
        loading = false
    }

    Modal(onDismiss = onClose, cardModifier = Modifier.borderRadius(8.px)) {
        Column(Modifier.gap(1.cssRem)) {
            // Header
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "Accept Trade",
                    Modifier.fontSize(1.125.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.text)
                )
                Spacer()
                SpanText(
                    "✕",
                    Modifier.fontSize(1.cssRem).color(SitePalette.textMuted)
                        .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                        .onClick { dismiss(onClose) }
                )
            }

            if (loading) {
                SpanText("Loading roster...", Modifier.color(SitePalette.textSecondary))
            } else if (roster != null) {
                val rosterData = roster!!

                // Calculate which sports need drops
                val incomingPlayers = trade.assets.filter {
                    it.fromClubId == trade.senderClubId && it.playerName != null
                }
                val outgoingPlayers = trade.assets.filter {
                    it.fromClubId == trade.receiverClubId && it.playerName != null
                }
                val outgoingPlayerIds = outgoingPlayers.mapNotNull { it.playerId }.toSet()

                // Group by sport
                val sportNeeds = mutableMapOf<String, Int>()
                val incomingBySport = incomingPlayers.groupBy { it.playerSport ?: "" }
                val outgoingBySport = outgoingPlayers.groupBy { it.playerSport ?: "" }

                for ((sport, incoming) in incomingBySport) {
                    val outCount = outgoingBySport[sport]?.size ?: 0
                    val activeSlotsForSport = rosterData.players.count {
                        it.sport == sport && it.rosterSlot != "LIV_LINE"
                    }
                    val totalSlotsForSport = when (sport) {
                        "MENS_GOLF" -> 5
                        "MENS_TENNIS" -> 4
                        "WOMENS_TENNIS" -> 2
                        else -> 0
                    }
                    val openSlots = totalSlotsForSport - activeSlotsForSport + outCount
                    val needed = incoming.size - openSlots
                    if (needed > 0) sportNeeds[sport] = needed
                }

                SpanText(
                    "You need to drop players to make room for incoming players:",
                    Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
                )

                // Show droppable players grouped by sport that needs drops
                for ((sport, count) in sportNeeds) {
                    val droppable = rosterData.players.filter {
                        it.sport == sport && it.rosterSlot != "LIV_LINE" && it.id !in outgoingPlayerIds
                    }
                    val selectedForSport = selectedDropIds.count { dropId ->
                        droppable.any { it.id == dropId }
                    }

                    Column(Modifier.gap(0.25.cssRem)) {
                        SpanText(
                            "${sportDisplayName(sport)} — drop $count player(s)",
                            Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
                        )

                        droppable.forEach { player ->
                            val isSelected = player.id in selectedDropIds
                            Row(
                                Modifier
                                    .fillMaxWidth()
                                    .backgroundColor(if (isSelected) Color.rgba(74, 124, 89, 0.15f) else Color.rgba(0, 0, 0, 0f))
                                    .border(1.px, LineStyle.Solid, if (isSelected) SitePalette.primary else SitePalette.border)
                                    .borderRadius(6.px)
                                    .padding(0.5.cssRem, 0.75.cssRem)
                                    .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                                    .onClick {
                                        if (isSelected) selectedDropIds.remove(player.id)
                                        else selectedDropIds.add(player.id)
                                    },
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                SpanText(player.name, Modifier.fontSize(0.875.cssRem).color(SitePalette.text))
                                Spacer()
                                SpanText(
                                    formatPoints(player.seasonPoints),
                                    Modifier.fontSize(0.875.cssRem).color(SitePalette.accent)
                                        .styleModifier { property("font-variant-numeric", "tabular-nums") }
                                )
                            }
                        }
                    }
                }

                val totalDropsNeeded = sportNeeds.values.sum()
                val canSubmit = selectedDropIds.size >= totalDropsNeeded && !submitting

                if (errorMessage != null) {
                    SpanText(errorMessage!!, Modifier.fontSize(0.75.cssRem).color(SitePalette.error))
                }

                Row(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                    // Cancel
                    Div(
                        Modifier
                            .backgroundColor(SitePalette.border)
                            .color(SitePalette.textSecondary)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                            .fontSize(0.875.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                            .onClick { dismiss(onClose) }
                            .toAttrs()
                    ) { Text("Cancel") }

                    Spacer()

                    // Accept
                    Div(
                        Modifier
                            .backgroundColor(if (canSubmit) SitePalette.primary else SitePalette.border)
                            .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                            .fontSize(0.875.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(if (canSubmit) com.varabyte.kobweb.compose.css.Cursor.Pointer else com.varabyte.kobweb.compose.css.Cursor.Default)
                            .onClick {
                                if (!canSubmit) return@onClick
                                scope.launch {
                                    submitting = true
                                    errorMessage = null
                                    try {
                                        val body = apiJson.encodeToString(
                                            AcceptTradeOfferRequest(trade.id, selectedDropIds.toList())
                                        )
                                        apiPost("/api/trades/accept", body, TradeOfferResponse.serializer())
                                        dismiss(onSuccess)
                                    } catch (e: ApiException) {
                                        errorMessage = e.errorMessage
                                    } catch (_: Exception) {
                                        errorMessage = "Failed to accept trade"
                                    }
                                    submitting = false
                                }
                            }
                            .toAttrs()
                    ) { Text(if (submitting) "Processing..." else "Accept Trade") }
                }
            }
        }
    }
}

@Composable
private fun TradeAssetRow(asset: TradeOfferAssetResponse, gain: Boolean) {
    val prefix = if (gain) "+" else "-"
    val color = if (gain) Color("#16a34a") else Color("#dc2626")
    Row(Modifier.gap(0.375.cssRem), verticalAlignment = Alignment.CenterVertically) {
        if (asset.playerName != null) {
            SpanText("$prefix${asset.playerName}", Modifier.fontSize(0.875.cssRem).color(color))
            if (asset.playerSport != null) {
                SportBadge(asset.playerSport)
            }
        } else if (asset.draftPickLabel != null) {
            SpanText("$prefix${asset.draftPickLabel}", Modifier.fontSize(0.875.cssRem).color(color))
        }
    }
}
